// FILENAME: /frontend/src/App.jsx
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import EnrollmentStatusPage from './pages/EnrollmentStatusPage.jsx';
import SettingsPage from './pages/SettingsPage';
import DealerPage from './pages/DealerPage';
import BuySellsPage from './pages/buySells/BuySellsPage.jsx';
import BillingReconciliationPage from './pages/billingReconciliation/BillingReconciliationPage.jsx';
import ReportsLandingPage from './pages/ReportsLandingPage';
import ReportViewPage from './pages/ReportViewPage';
import DataStoryPage from './pages/DataStoryPage';
import DailyDigestSectionPage from './pages/DailyDigestSectionPage';
import AppsheetAccountsPage from './pages/AppsheetAccountsPage.jsx';
import AppsheetOrderItemsPage from './pages/AppsheetOrderItemsPage.jsx';
import WipIndexPage from './pages/wip/WipIndexPage.jsx';
import { wipPages } from './pages/wip/wipRegistry.js';
import { areE2ERoutesEnabled, e2ePageRoutes } from './routes/e2eRouteRegistry.jsx';
import { getSharedData, handleShareTarget, parseBacFromText } from './utils/shareTargetHandler.js';
import { useSearch } from './context/SearchContext.jsx';
import { useDashboard } from './context/DashboardContext.jsx';
import { logger } from './utils/logger.js';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setSearchTerm, performSearch } = useSearch();
  const { searchType, searchValue, setCurrentTab, setSearchType, setSearchValue } = useDashboard();
  const e2eRoutesEnabled = areE2ERoutesEnabled();

  // Handle Web Share Target on mount
  useEffect(() => {
    const sharedData = getSharedData();
    if (sharedData && (sharedData.text || sharedData.title || sharedData.url)) {
      const result = handleShareTarget(sharedData, navigate);
      if (result.action === 'navigate' && result.path) {
        // Navigation handled by handleShareTarget
        logger.info('[App] Handled share target', result);
      }
    }
  }, [navigate]);

  // Listen for extension-driven searches (no navigation / refresh needed if GMER is already open).
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.BroadcastChannel === 'undefined') return undefined;
    const channel = new BroadcastChannel('gmer-extension');

    const onMessage = (event) => {
      const data = event?.data || {};
      if (data?.type !== 'SEARCH_BAC') return;
      const bac = String(data?.bac || '').trim();
      if (!bac) return;

      // Ensure dashboard state reflects a BAC search.
      setCurrentTab?.(0);
      setSearchType?.('bac');
      setSearchValue?.(bac);

      // Also run global search so the user sees results immediately (and this matches URL-based behavior).
      setSearchTerm?.(bac);
      void performSearch?.(bac);

      // Update the URL without a reload so the state is visible/shareable.
      const next = new URLSearchParams(window.location.search || '');
      next.set('action', 'search');
      next.set('search', bac);
      next.set('type', 'bac');
      next.set('value', bac);
      next.set('tab', '0');
      navigate(`/enrollment-status?${next.toString()}`, { replace: true });
    };

    channel.addEventListener('message', onMessage);
    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [navigate, performSearch, setCurrentTab, setSearchTerm, setSearchType, setSearchValue]);

  // URL-driven actions (keep params in the URL for user reference).
  // Supported:
  // - ?action=dealer&bac=123456            -> navigates to /dealer/:bac
  // - ?action=search&term=...              -> stays on dashboard (no forced navigation)
  // - ?action=filter&program=...&referenceId=... -> stays on dashboard (no forced navigation)
  // - ?search=123456                       -> for BAC-like terms: routes to dashboard BAC view (?type=bac&value=...)
  useEffect(() => {
    const action = searchParams.get('action');
    const bacParam = searchParams.get('bac');
    const termParam = searchParams.get('term') || searchParams.get('search') || '';

    const ensureEnrollmentStatusWithCurrentQuery = () => {
      if (location.pathname !== '/enrollment-status') {
        navigate(`/enrollment-status${location.search || ''}`, { replace: true });
      }
    };

    // Explicit dealer navigation only.
    if (action === 'dealer') {
      const bac = (bacParam || '').trim() || parseBacFromText(termParam);
      if (bac) {
        const targetPath = `/dealer/${encodeURIComponent(bac)}${location.search || ''}`;
        if (location.pathname + (location.search || '') !== targetPath) {
          navigate(targetPath, { replace: true });
        }
      }
      return;
    }

    // Any BAC-like term should route to dashboard BAC search, not dealer page.
    // This avoids getting "stuck" on /dealer when `?search=` remains in the URL.
    const bacFromTerm = parseBacFromText(termParam);
    if (bacFromTerm) {
      const normalizedBac = String(bacFromTerm).trim();

      if (searchType !== 'bac' || String(searchValue || '').trim() !== normalizedBac) {
        setCurrentTab?.(0);
        setSearchType?.('bac');
        setSearchValue?.(normalizedBac);
      }

      // Ensure we're on dashboard.
      ensureEnrollmentStatusWithCurrentQuery();

      // If URL already expresses the bac dashboard search, don't rewrite it.
      const currentType = searchParams.get('type');
      const currentValue = searchParams.get('value');
      const matches = currentType === 'bac' && String(currentValue || '').trim() === normalizedBac;
      if (!matches) {
        const next = new URLSearchParams(searchParams);
        next.set('type', 'bac');
        next.set('value', normalizedBac);
        // Keep action/search params as-is for user reference.
        const nextSearch = next.toString();
        if (location.search !== `?${nextSearch}`) {
          navigate(`/enrollment-status?${nextSearch}`, { replace: true });
        }
      }
      return;
    }

    // For filter/search actions, we only ensure dashboard (no additional redirects).
    if (action === 'filter' || action === 'search') {
      ensureEnrollmentStatusWithCurrentQuery();
      return;
    }
  }, [location.pathname, location.search, navigate, searchParams, searchType, searchValue, setCurrentTab, setSearchType, setSearchValue]);

  // Handle app shortcuts (launch_queue API)
  useEffect(() => {
    if ('launchQueue' in window && 'LaunchParams' in window) {
      window.launchQueue.setConsumer((launchParams) => {
        if (launchParams.targetURL) {
          try {
            const url = new URL(launchParams.targetURL, window.location.origin);
            if (url.origin === window.location.origin) {
              navigate(url.pathname + url.search);
            }
          } catch {
            // Invalid URL, ignore
          }
        }
      });
    }
  }, [navigate]);

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route index element={<HomePage />} />
        <Route path="enrollment-status" element={<EnrollmentStatusPage />} />
        <Route path="story" element={<DataStoryPage />} />
        <Route path="daily-digest/section/:section" element={<DailyDigestSectionPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="reports" element={<ReportsLandingPage />} />
        <Route path="reports/:id" element={<ReportViewPage />} />
        <Route path="buy-sells" element={<BuySellsPage />} />
        <Route path="billing-reconciliation" element={<BillingReconciliationPage />} />
        <Route path="appsheet/accounts" element={<AppsheetAccountsPage />} />
        <Route path="appsheet/orderitems" element={<AppsheetOrderItemsPage />} />
        <Route path="dealer/:bac" element={<DealerPage />} />
        {e2eRoutesEnabled &&
          e2ePageRoutes.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <Suspense fallback={null}>
                  <Component />
                </Suspense>
              }
            />
          ))}
        <Route path="__wip" element={<WipIndexPage />} />
        {wipPages.map((page) => {
          const PageComponent = page.component;
          return <Route key={page.path} path={page.path} element={<PageComponent />} />;
        })}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return <AppContent />;
}

export default App;
